/* Hand-authored enemy decode lines — the tooltip-canon grammar applied to the
   combat roster (Edward sign-off pass, 2026-06-11).

   One entry per enemy, keyed by the FE resolution key (slug first, then
   boss_class — see decodeForEnemy).

   `entity`  = the canonical medical/pharmacological entity this enemy
               represents (Edward 2026-06-11). Rendered as a subtitle under the
               name on the announce card and in the tooltip — "what it IS",
               reasoned from the mechanics, NOT a flavor phrase. Preserves
               medical casing (LDL, C. difficile, Lipoprotein(a)) — do NOT
               uppercase at the renderer.
   `announce`= an array of token LINES rendered by GameTip/decode.jsx — the
               MECHANIC, "what it DOES". Big on the pre-combat announce card,
               reused (in flat tip mode) by the enemy-strip + sim-tile tooltips.
   `attack`  = the clinically grounded signature attack name.
   `deals`   = one token line spelling the engine-grounded damage formula.
   `dealsByFormula` = optional damage_formula-specific replacements for encounters
               where one enemy identity has distinct solo/simul attack specs.

   Authoring law (spec: wiki/decisions/2026-06-11-tooltip-canon.md):
   - the entity is the identity line; the announce lines are the consequence
   - tag tokens are the TRUE HUD pills — the threat the player will literally see
   - glyph-stat pairs carry every number; words are connective tissue
   - ≤7 words per line, no periods, no articles where avoidable

   Reasoning trail (mechanic → entity) for the entity field lives in
   wiki/decisions/tooltip-canon-entity-reasoning.md.

   Source prose lives in backend mini_boss_engine/registry.py ability_summary +
   mid_boss_roster.py descriptions + simultaneous_pool/*.py — these lines DECODE
   that mechanic, they do not replace it as the mechanic of record. */

// ── token shorthands ────────────────────────────────────────────────────────
const tag = (t, v) => (v === undefined ? { tag: t } : { tag: t, v });
// A tag whose MULTIPLIER comes from the enemy's SERIALIZED attack spec
// (`stack_delta`) instead of being authored here. Two cases, and the second is the
// general one: (1) an enemy applying a different number of stacks in its solo vs
// simul variant cannot state one literal truthfully (Natrix); (2) ANY authored
// literal duplicates a backend number in the frontend, and the two drift the moment
// the roster is retuned — so prefer this over `tag(word, '+N')` on every load row,
// which is why the Athero pool's two moved to it.
// enemyTooltipSections resolves this against opts.attackSpec,
// expanding the token into the SAME `num() op('×') tag()` formula the Ten Shin aura
// row and the warden Deals rows already use. A delta of 1 stays a bare chip (a
// prefix reading "1 ×" is noise).
const tagStack = (t) => ({ tag: t, fromStackDelta: true });
const g = (k, v) => (v === undefined ? { g: k } : { g: k, v });
// An HP number as a status-tag CHIP inline in the tooltip (Edward 2026-07-14):
// same light-chip chrome as the status tags, hugging its short value. Use for any
// inline HP value in decode text (regen +5, permanent max-HP loss −5 max). Bars are
// untouched — they render HP with the bare glyph, not this chip.
const hp = (v) => ({ g: 'hp', v, chip: true });
const j = (x) => ({ j: x });
const t = (x) => ({ t: x });
// A damage numeral in an authored "Deals ..." row — rendered bold + enemy-red so
// the actual damage values anchor the line while the connective words recede.
const num = (x) => ({ n: x });
// A math operator (+, ×, parens) in a damage formula — styled distinct from the
// numerals and the connective words (the muted math tone). Bake needed spacing into
// the operator string (e.g. ' + (') so the formula reads naturally in inline flow.
const op = (x) => ({ op: x });
// A short clinical LABEL naming a damage component in a "Deals ..." formula — turns a
// bare constant into named pathophysiology so the row teaches (Edward 2026-07-13,
// max-clinical pass). E.g. num('1'), clin('vasoconstriction') reads the flat hit as
// angiotensin-II direct vasoconstriction, distinct from the pressure-scaled term.
// Rendered dim + small + lowercase so it recedes behind the red numerals and the pill.
const clin = (x) => ({ clin: x });
// A labeled causal edge — the verb rides ON the arrow as a semantic graph edge
// (Edward 2026-06-11). `edge('Hardens')` renders "—Hardens→".
const edge = (x) => ({ edge: x });
const dispense = () => ({ dispense: true });

export const ENEMY_DECODE = {
  // ── Athero pool minis — the lipoprotein cascade feeding plaque ─────────────
  // 🔴 THE TWO LOAD ROWS NOW CARRY A VERB AND A SERIALIZED MAGNITUDE (2026-08-15).
  // Both were authored as `[tag('lipid', '+N')]` — a bare pill with no lead verb
  // and a backend number copied by hand — and the tooltip path drew NEITHER of
  // them: `decodeToInflicts` keeps only the lead, the chip and the math tokens
  // sitting BEFORE the chip (decode.jsx:283-303), and it drops a tag's authored
  // `v` deliberately because "the status chip's def is the single source of truth
  // for the number" (decode.jsx:253). `lipid` had no def, so the magnitude was
  // surrendered to a def that did not exist and the row painted as an orphan pill
  // beside an empty right column. The def is added at statusTags.jsx:23; the
  // count now comes from the enemy's OWN serialized `stack_delta` via `tagStack`,
  // the same route Natrix already uses, so the tooltip cannot disagree with the
  // engine when the roster is retuned (chylo 2 / veldra 1, registry.py:35, :64).
  // `Inflicts` is the verb every other status-writing row in this file leads with.
  // ⚠️ `j('per round')` is engine-true (registry.py:39, :67) and is kept as the
  // authored fact, but it does NOT render and did not before: the single-chip
  // descriptor discards every token after the chip. Do not "fix" its absence here.
  // Chylo's damage moves into the DEALS row where every other enemy in the file
  // states its damage. It was an announce stat row, so it rendered as a bare glyph
  // pair and, being a `t()` fragment, took the panel's Title Case: "0 · Feeds The
  // Cascade" beside five siblings all reading "Deals n <lowercase label>". Its
  // words are unchanged and still verbatim from registry.py:39 — they are just a
  // `clin()` label now, which is what a named damage component IS in this grammar.
  // Warrant: `damage_formula: "fixed_zero"` (registry.py:36). Zero is his whole
  // point, so the Deals row states it rather than omitting it.
  chylo: { entity: 'Chylomicrons', attack: 'Chylomicron Load', announce: [
    [t('Inflicts'), tagStack('lipid'), j('per round')],
  ], deals: [t('Deals'), num('0'), clin('feeds the cascade')] },
  veldra: { entity: 'VLDL', attack: 'Triglyceride Lash', announce: [
    [t('Inflicts'), tagStack('lipid'), j('per round')],
  ], deals: [t('Deals'), num('2'), op('×'), tag('lipid'), op('+'), num('1'), clin('triglyceride influx')] },
  idol: { entity: 'IDL', attack: 'Density Shed', announce: [],
    deals: [t('Deals'), num('2'), op('×'), tag('lipid')] },
  // Eldra's second row gains a LEAD VERB, and that one token is what makes it
  // render at all. The lead selector takes the FIRST `t()` token anywhere in the
  // line (decode.jsx:286), so with `t('permanent')` as the only one, "permanent"
  // was being promoted in front of the pill and the row painted as the run-together
  // "permanentplaque" — with the `hp('−5 max')` silently dropped behind it, because
  // the single-chip descriptor keeps nothing after the chip. The magnitude now
  // lives in `plaque`'s own def (statusTags.jsx), which is where this grammar says
  // a status' number belongs, so the row reads `Inflicts [Plaque] | −5 max permanent`
  // and the permanent cost reaches a screen for the first time.
  // ⚠️ The trailing tokens are KEPT, not moved, because decode.test.jsx uses this entry
  // as its fixture for every token shape.
  // 🔴 THE OTHER HALF OF THIS NOTE WAS FALSE AND IS STRUCK (checked 2026-08-15). It read
  // "the announce CARD renders the full token line (DecodeLines does draw `hp` and `j`)".
  // NOTHING passes `mode="announce"` anywhere in frontend/src outside tests, and the one
  // non-test importer of DecodeLines (pages/Compendium/content/mechanics.jsx) calls it
  // only on `decode.deals`. Every announce surface routes through
  // enemyTooltipSections -> decodeToInflicts, so a token the collapse discards reaches
  // NO screen at all. Do not defend a trailing token on the grounds that the card draws
  // it; put the fact in the status' `def`, which is where this grammar says it lives.
  //
  // 🔴 THE CONVERTER ROW IS NOW `Converts [x] into [y]`, THE FORM ESCARA ALREADY USES
  // (Jon, 2026-08-15). It read `Adds [lipid] —Hardens→ [plaque]`, and that was the
  // last thing separating this cohort's cards from Ten Shin's. Two reasons it is a
  // correction and not just a match:
  //   • `Adds` CONTRADICTED THE ENGINE SIGN. eldra's `stack_delta` is -1 — a removal
  //     — with `plaque_delta: +1`, and its own ability_summary reads "Consumes 1
  //     lipid stack → applies permanent plaque". The row said it ADDS the thing it
  //     spends. `Converts` is the verb this file already uses for exactly that shape.
  //   • The connector was the `edge('Hardens')` rail, which decode.jsx offered as a
  //     per-enemy voice. Escara's own comment records the opposite preference for a
  //     conversion ("a two-chip row joined by the PLAIN word `into`, no causal-arrow
  //     rail"), so the two converters in the game disagreed on form. They agree now.
  // ⚠️ The `'1'` magnitudes STAY. They are engine-exact (one stack in, one plaque
  // out), the convert branch never reads them anyway, and the announce CARD does
  // draw them. Jon asked for the wording; the counts are not wording.
  // ⚠️ `edge()` is NOT dead after this — `phantom` still uses `edge('Bites')`. But no
  // CONVERTER row uses one any more, which is why decode.jsx's comment moved with it.
  eldra: { entity: 'LDL Cholesterol', attack: 'Plaque Deposit', announce: [
    [j('Converts'), tag('lipid', '1'), j('into'), tag('plaque', '1')],
    [t('Inflicts'), tag('plaque'), hp('−5 max'), j('·'), t('permanent')],
  ], deals: [t('Deals'), num('2'), clin('endothelial injury')] },
  // The streak-immunity row is now a VERB + PILL like every other announce row,
  // instead of the bare Title-Cased fragment "Streak-Immune" it painted as. The
  // pill already existed (`streak` in TAG_META) and was simply never used as a tag
  // token anywhere. Warrant: `player_streak_immune: True` (registry.py:156), read
  // at batch_combat_engine.py:6658 as `remaining_without if player_streak_immune`,
  // i.e. the streak bonus is excluded from what lands. "Ignores" is a new verb in
  // this file, and deliberately so: it is the only enemy-to-PLAYER-BUFF relation in
  // the roster, and none of the existing verbs (Inflicts / Converts / Scales with)
  // describes declining someone else's bonus.
  // ⚠️ NO right column here, and that is correct rather than unfinished: `streak` is
  // a player-side resource, and the block at statusTags.jsx states those carry a
  // `line` only and never an Inflicts `def`. Do not add one to make this row match
  // its neighbours.
  'lipo-alpha': { entity: 'Lipoprotein(a)', attack: 'Lp(a) Amplification', announce: [
    [t('Ignores'), tag('streak')],
  ], deals: [t('Deals'), tag('lipid'), op('×'), tag('plaque')] },

  // ── Anaphyl pool minis — the four Gell-Coombs hypersensitivity types ───────
  // hyst moves to `tagStack` for the reason stated at the top of this file: an authored
  // literal duplicates a backend number in the frontend and the two drift on a retune.
  // Warrant `stack_delta: 2` (mini_boss_engine/registry.py:184). ⚠️ submic, spectra and
  // latanz must NOT follow: all three carry `stack_delta: 0` (:307, :343, :274) and
  // hold their loads in other fields, so `tagStack` would read the wrong field and
  // silently degrade each to a bare chip. hyst is the only legitimate candidate here.
  hyst: { entity: 'Type I Hypersensitivity', attack: 'IgE Cross-Link', announce: [
    [t('Inflicts'), tagStack('histamine')],
  ], deals: [t('Deals'), tag('histamine')] },
  // The orphan `[t('permanent')]` row is folded into the line it qualifies. Alone it
  // was a whole tooltip row holding one Title-Cased word, floating under the mechanic
  // it modifies. lyse stays the canonical case-2 bespoke row (no reusable chip for a
  // raw max-HP loss), and the fallback branch keeps every token, so nothing is lost;
  // the tail now matches eldra's shipped `hp · permanent` shape.
  lyse: { entity: 'Type II Hypersensitivity', attack: 'Antibody Cytotoxicity', announce: [
    [t('Inflicts'), g('down'), hp('3 max'), j('per round'), j('·'), t('permanent')],
  ], deals: [t('Deals'), num('2'), clin('opsonized cell lysis')] },
  plex: { entity: 'Type III Hypersensitivity', attack: 'Immune Complex', announce: [
    [t('Inflicts'), tag('deposit')],
  ], deals: [t('Deals'), num('3'), clin('vascular inflammation')] },
  latanz: { entity: 'Type IV Hypersensitivity', attack: 'T-Cell Delay', announce: [
    [t('Inflicts'), tag('delayed', '6')],
  ], deals: [t('Deals'), num('6'), clin('delayed inflammation'), t('in 2 rounds')] },

  // ── Pestil pool minis — antimicrobial resistance & stewardship ─────────────
  submic: { entity: 'Subtherapeutic Dosing', attack: 'Sub-MIC Exposure', announce: [
    [t('Inflicts'), tag('resistance pressure', '+1'), j('per wrong answer')],
  ], deals: [t('Deals'), num('2'), clin('selection injury')] },
  spectra: { entity: 'Broad-Spectrum Overuse', attack: 'Flora Depletion', announce: [
    [t('Inflicts'), tag('cdiff', '+1'), j('per correct strike')],
  ], deals: [t('Deals'), num('3'), clin('microbiome injury')] },
  veilfilm: { entity: 'Bacterial Biofilm', attack: 'Biofilm Barrier', announce: [
    [t('Inflicts'), g('down'), g('damage', '30–100%'), j('on your strikes')],
    [t('scales with'), tag('resistance pressure')],
  ], deals: [t('Deals'), num('3'), clin('persistent infection')] },
  // Same edit as lipo-alpha's, and it lands here for consistency rather than for
  // Pestil's sake: these are the only two streak-immune enemies in the game
  // (registry.py:156, :278) and they carried the identical authored string, so
  // changing one and not the other would invent a difference between two cards
  // that state the same mechanic.
  reservor: { entity: 'Infection Reservoir', attack: 'Latent Reactivation', announce: [
    [t('Inflicts'), tag('reservoir', '+1')],
    [t('Ignores'), tag('streak')],
  ], deals: [t('Deals'), num('3'), clin('reservoir seeding')] },

  // ── Bradyx pool minis — Parkinson's disease & dopaminergic therapy ─────────
  // 🔴 THE BRADYX / CONVUL / ALGO POOLS GAINED THE INFLICTS GRAMMAR ON 2026-08-15, the
  // same pass the Athero pool had. Three defects ran through all three pools and every
  // one of them was invisible to a reader as a defect - the cards looked merely thin:
  //   (1) A status with no `def` paints an EMPTY right column, because decode.jsx:253
  //       drops a tag's authored `v` on the stated grounds that the def is the single
  //       source of the number. Seven statuses had none. Fixed in statusTags.jsx.
  //   (2) The lead is `toks.find(t => t.t !== undefined)` - the first t() ANYWHERE in
  //       the line, INCLUDING after the chip - so a trailing clause was promoted in
  //       front of the pill. bradykin painted "Levodopa 3-On / 1-Off [Pulse]".
  //   (3) The single-chip branch DISCARDS every token after the chip.
  // bradykin row 0 had (2) and (3) at once. The verb anchors the lead so the trailing
  // clause stops being promoted; pulse's new def carries the fact to the right column.
  // ⚠️ The pair is atomic. The verb alone deletes "levodopa 3-on / 1-off" from every
  // surface; the def alone duplicates it as both lead and right column.
  bradykin: { entity: 'Levodopa Wearing-Off', attack: 'Bradykinetic OFF', announce: [
    [t('Inflicts'), tag('pulse'), t('levodopa 3-on / 1-off')],
    [t('missed OFF'), j('→'), tag('freeze')],
  ], deals: [t('Deals'), num('4'), clin('wearing-off transition'), t('or'), num('6'), clin('OFF-state bradykinesia')] },
  tremor: { entity: 'Parkinsonian Tremor', attack: 'Resting Tremor', announce: [
    [t('hits: anticholinergic · DA agonist · levodopa')],
    [t('anticholinergic in elderly'), j('→'), tag('confusion')],
  ], deals: [t('Deals'), num('3'), clin('resting oscillation')] },
  // The digit comes out of the voice and into the token grammar, and the row mounts the
  // real HUD chip the player is already watching (PlayerDoseTag). Warrant
  // `spawn_trigger: levodopa_strike_count_ge_6` (registry.py:520); the `6+` keeps the
  // engine's own qualifier, since a batch round can cross the threshold at 7.
  // ⚠️ `levodopa` gets NO def and its empty right column is CORRECT - it is a
  // player-side resource, the same rule that keeps `streak` bare. See statusTags.jsx.
  dyskinex: { entity: 'Levodopa-Induced Dyskinesia', attack: 'Peak-Dose Dyskinesia', announce: [
    [t('spawns at'), num('6+'), tag('levodopa')],
  ], deals: [t('Deals'), num('5'), clin('choreoathetosis'), t('per levodopa attempt')] },
  hallux: { entity: "Parkinson's Disease Psychosis", attack: 'Dopamine Overload', announce: [
    [t('spawns at 4+ DA agonist / 8+ levodopa')],
    [t('hits: pimavanserin · quetiapine · clozapine')],
  ], deals: [t('Deals'), num('4'), clin('psychosis'), t('or'), num('7'), clin('D2 blockade')] },

  // ── Convul pool minis — epilepsy & antiepileptic pharmacology ──────────────
  // 🔴 `Raises`, NOT `Inflicts`, and the distinction is the whole point of the row.
  // `seizure` is the BOSS'S OWN INVULNERABILITY GAUGE, not a debuff worn by the player:
  // Convul is invulnerable while the threshold is above zero, and idiopra's own spec is
  // `increases_seizure_threshold: 1` (registry.py:590). `Inflicts` would state the
  // wrong direction on a public page. `Raises` is already this file's verb for a gauge
  // going up (glycor) and is the roster's own word (mid_boss_roster.py:90, "Raises the
  // seizure threshold"). This row had NO lead at all, so the pill sat orphaned and the
  // card said literally nothing: measured live as a bare "Seizure" with an empty right
  // column, its authored `+1` dropped by the collapse and its `per round unstruck`
  // discarded. Both facts now reach the screen through seizure's def.
  idiopra: { entity: 'Idiopathic Epilepsy', attack: 'Focal Discharge', announce: [
    [t('Raises'), tag('seizure', '+1'), j('per round unstruck')],
    [t('AED strike'), j('→'), tag('seizure', '−2')],
  ], deals: [t('Deals'), num('3'), clin('cortical discharge')] },
  // `Demands` is the engine's own verb ("Demands the correct protocol order",
  // registry.py:634) and the roster's (mid_boss_roster.py:90), and it already ships in
  // this file on ulcra. It is used on BOTH protocol rows rather than mixing it with
  // `Inflicts` on the pool lead: one chip carrying two verbs across two cards in one
  // pool is the same failure the eldra/athero converter comment records below.
  // Without the lead, "benzo IV then AED in" was promoted in front of the pill and the
  // `rounds 2` was discarded outright.
  stata: { entity: 'Status Epilepticus', attack: 'Status Cascade', announce: [
    [t('spawns at'), tag('seizure', '5')],
    [t('Demands'), tag('protocol'), t('benzo IV then AED in'), g('rounds', '2')],
  ], deals: [t('Deals'), num('12'), clin('status epilepticus')] },
  // 🔴 ROW 0 DELIBERATELY LOSES ITS TAG. It is the dose SCHEDULE, and it inflicts
  // nothing - the status is applied by row 1, above the window. Keeping the tag on both
  // rows made `decodeToInflicts` draw toxicity's def TWICE on one card (it has no
  // dedupe), and pinned an "under / in window" subject to an "above the window" right
  // column that contradicts it. Untagged, row 0 takes the fallback branch as flat stats
  // - the shape inductra and nocir already use - and keeps every word it had.
  toxica: { entity: 'AED Toxicity', attack: 'Toxic Level', announce: [
    [t('dose window: under = 0 · in = full')],
    [t('over window'), j('→'), tag('toxicity')],
  ], deals: [t('Deals'), num('5'), clin('AED toxicity'), t('above window')] },
  inductra: { entity: 'Enzyme-Inducing AEDs', attack: 'CYP Induction', announce: [
    [t('while alive: AED'), g('damage', '75%'), j('on the others')],
    [t('hits: non-inducing AEDs only')],
  ], deals: [t('Deals'), num('3'), clin('breakthrough excitation')] },

  // ── Algo pool minis — pain pathways & analgesia ────────────────────────────
  nocir: { entity: 'Nociceptive Pain', attack: 'Somatic Pain', announce: [
    [t('hits: NSAID / APAP full'), j('·'), t('opioid 60%')],
  ], deals: [t('Deals'), num('3'), clin('nociceptor firing')] },
  neuralg: { entity: 'Neuropathic Pain', attack: 'Neuropathic Burst', announce: [
    [t('hits: gabapentinoid / TCA / SNRI'), j('·'), t('opioid 0')],
  ], deals: [t('Deals'), num('4'), clin('ectopic nerve firing')] },
  // The causal half of this sentence never rendered: both j() tokens sat between the
  // lead and the chip and the single-chip collapse keeps only num/op there, so the card
  // read "Opioid Full [Tolerance]" - two facts with the "but each" that connects them
  // deleted. One token merge restores it. Wording is registry.py:810-813 compressed
  // ("Opioid strikes deal full damage but each adds 1 opioid tolerance stack").
  visceralg: { entity: 'Visceral Pain', attack: 'Visceral Throb', announce: [
    [t('opioid full but each builds'), tag('tolerance', '+1')],
  ], deals: [t('Deals'), num('5'), clin('visceral afference')] },
  withdraw: { entity: 'Opioid Withdrawal', attack: 'Withdrawal Surge', announce: [
    [t('spawns at 5+ opioid strikes')],
  ], deals: [t('Deals'), num('2'), op('×'), t('opioid strikes')] },

  // ── Mid bosses (announce lead for pool fights; key = boss class) ──────────
  anaphyl: { entity: 'Hypersensitivity Reactions', attack: 'Anaphylactic Crisis', announce: [
    [t('Inflicts'), tag('histamine')],
    [t('Inflicts'), tag('deposit')],
    [t('Inflicts'), tag('delayed')],
  ], deals: [t('Deals'), num('10'), clin('hypersensitivity injury'), t('or'), num('30'), clin('fifth-round crisis')] },
  pestil: { entity: 'Antimicrobial Resistance', attack: 'Resistance Crisis', announce: [
    [t('Inflicts'), tag('resistance pressure')],
    [t('Inflicts'), tag('cdiff')],
    [t('Inflicts'), tag('reservoir')],
  ], deals: [t('Deals'), num('10'), clin('infectious burden'), t('or'), num('30'), clin('fifth-round sepsis')] },
  // 🔴 THESE TWO WERE THE ONLY THREE-CHIP ROWS IN THE FILE AND BOTH WERE SILENTLY
  // EATING THEIR MIDDLE CHIP. Three tags that resolve in either registry trip the
  // CONVERTER branch (decode.jsx:276), which keeps the FIRST and the LAST chip and
  // drops everything between - so an "A · B · C" listing painted as "A · · C", with
  // the two separators left stranded around the hole. Measured live before the fix:
  // bradyx read "Pulse · · Confusion" (Freeze gone) and convul read
  // "Seizure · · Toxicity" (Status Protocol gone), each with no right column at all.
  // The fix is three ordinary Inflicts-shaped rows, which is the form the other four
  // pool leads (anaphyl, pestil) already use for exactly this - a list of what the pool
  // does to you. Land them together so the two leads cannot disagree in form.
  bradyx: { entity: "Parkinson's Disease", attack: 'Motor Freeze', announce: [
    [t('Inflicts'), tag('pulse')],
    [t('Inflicts'), tag('freeze')],
    [t('Inflicts'), tag('confusion')],
  ], deals: [t('Deals'), num('10'), clin('motor rigidity'), t('or'), num('30'), clin('fifth-round akinesia')] },
  // 🔴 THREE DIFFERENT VERBS HERE, AND `Inflicts` WOULD BE FALSE TWICE. They are
  // mid_boss_roster.py:90 verbatim - "Raises the seizure threshold, demands protocol
  // order, courts toxicity" - which is the game's own sentence about this boss.
  // `seizure` is the pool's own invulnerability gauge, which it RAISES (see idiopra
  // above); `toxicity` is SELF-inflicted by the player's own dose
  // (batch_combat_engine.py:4663), so nothing inflicts it on anyone.
  // ⚠️ `Courts` is a NEW verb in this file, declared here the way `Ignores` is declared
  // on lipo-alpha: no existing verb describes an enemy that creates the conditions for
  // the player's own self-harm. It occurs exactly once, so there is no second site to
  // keep in step.
  convul: { entity: 'Epilepsy', attack: 'Cortical Discharge', announce: [
    [t('Raises'), tag('seizure')],
    [t('Demands'), tag('protocol')],
    [t('Courts'), tag('toxicity')],
  ], deals: [t('Deals'), num('10'), clin('seizure activity'), t('or'), num('30'), clin('fifth-round status')] },
  algo: { entity: 'Chronic Pain', attack: 'Pain Crisis', announce: [
    [t('pain pathways numbed by'), tag('tolerance')],
  ], deals: [t('Deals'), num('10'), clin('pain flare'), t('or'), num('30'), clin('fifth-round crisis')] },
  // Athero's row takes the same converter form as eldra's above, for the same
  // reason: it is the SAME cascade stated at the pool's altitude, and a boss whose
  // row disagrees in form with the mini that performs the mechanic reads as two
  // different mechanics. He has no `attack_spec` of his own (no entry in the mini
  // registry, and batch_combat_engine gives every mid-boss class a flat damage
  // table), so this row has always described what his POOL does rather than a spec
  // of his own — which is the convention for all six pool leads in this file.
  athero: { entity: 'Atherosclerosis', attack: 'Plaque Rupture', announce: [
    [j('Converts'), tag('lipid'), j('into'), tag('plaque')],
  ], deals: [t('Deals'), num('10'), clin('endothelial injury'), op('·'), num('30'), clin('plaque rupture'), t('every 5th')] },
  // Simultaneous pools — gauge-driven mains. Universal engine rule (verified
  // 2026-06-11): every sim main takes 0 damage while its minis are alive, so
  // line 1 is the shield warning, uniform across all ten.
  glyca: { entity: 'Diabetes Mellitus', attack: 'Glucose Disequilibrium', announce: [
    [tag('mini-shield')],
    [t('Runs'), tag('glucose')],
  ] },
  hemora: { entity: 'Anticoagulation (INR Control)', attack: 'Coagulation Breach', announce: [
    [tag('mini-shield')],
    [t('Runs'), tag('inr')],
  ] },
  synap: { entity: 'Psychiatric Disorders', attack: 'Treatment Withholding', announce: [
    [tag('onset-shield')],
  ], deals: [t('Deals'), num('2'), op('×'), tag('onset-shield'), op('+'), num('5'), clin('treatment delay')] },
  coelo: { entity: 'Gastrointestinal Disease', attack: 'Complication Cascade', announce: [
    [tag('mini-shield')],
  ] },
  mitos: { entity: 'Cancer (Clonal Proliferation)', attack: 'Clonal Expansion', announce: [
    [tag('mini-shield')],
  ] },
  gravida: { entity: 'Pregnancy Drug Safety', attack: 'Gestational Attrition', announce: [
    [tag('mini-shield')],
    [t('Advances'), tag('gestational-week')],
  ], deals: [t('Deals'), num('2'), clin('gestational attrition'), t('while minis live')] },
  chroni: { entity: 'Chronic Obstructive Pulmonary Disease', attack: 'Airflow Obstruction', announce: [
    [tag('mini-shield')],
  ] },
  nephra: { entity: 'Chronic Kidney Disease', attack: 'Filtration Decline', announce: [
    [tag('mini-shield')],
  ] },
  cephalgia: { entity: 'Migraine', attack: 'Migraine Recurrence', announce: [
    [tag('mini-shield')],
  ] },
  visia: { entity: 'Ocular Disease', attack: 'Visual Veil', announce: [
    [tag('mini-shield')],
  ] },
  // Ten Shin carries Resistant from the start and regenerates +5 HP each round.
  // Other enemies develop Resistant after absorbing two dispenses.
  ten_shin: {
    entity: 'Resistant Hypertension',
    announce: [
      [t('Develops'), tag('resistant'), hp('+5'), t('/ round')],
    ],
    aura: [
      t('Enemies develop'), tag('resistant'), t('after absorbing'),
      num('2'), op('×'), dispense(),
    ],
  },
  // Ten Shin axis wardens — the HTN standalone class-mini fights are SOLO vs one
  // warden, so each needs its own tooltip. Keyed by class slug (raas_warden…) so
  // decodeForEnemy resolves via boss_class. Copy tracks each warden's SOLO
  // attack_spec (backend services/ten_shin_roster.py).
  // HTN axis wardens — each SOLO class-mini fight now decodes to the TRUE status
  // chip it inflicts (all-statuses-as-chips, Edward 2026-07-12). raas/calcium/
  // volume all load the player's `pressure` stack (the chip's def carries the
  // number); aldosterone inflicts `end-organ` (permanent max-HP loss). Adrenergic
  // has no reusable status, so it stays the legit case-2 bespoke stat row.
  // Deals row is the COMPONENT SPLIT (Edward 2026-07-13, max-clinical/min-UX): the
  // engine's `1 + 2P` is named as its two real mechanisms on ONE line. CONVENTION
  // (locked here as the template for all enemies): lead with the TAG-SCALED term
  // (`2 × [pressure]` — the actionable, load-driven hit), then trail the flat constant
  // LAST as `+ n <clinical label>` (`+ 1 vasoconstriction` — the direct angiotensin-II
  // hit). Numerals num(), operators op('×')/op('+') (× not letter x), status pill
  // tag(), constant named by clin(). Engine unchanged (1 + 2*P off the pre-load
  // snapshot, then +1 load) so no balance/ordering shift; the live NEXT number carries
  // the exact total.
  raas_warden: { entity: 'RAAS Activation', attack: 'Angiotensin Surge', announce: [
    [t('Inflicts'), tag('pressure')],
  ], deals: [t('Deals'), num('2'), op('×'), tag('pressure'), op('+'), num('1'), clin('vasoconstriction')] },
  calcium_gate: { entity: 'Arteriolar Tone', attack: 'Afterload Clamp', announce: [
    [t('Inflicts'), tag('pressure')],
  ], deals: [t('Deals'), num('2'), op('×'), tag('pressure')] },
  // Natrix loads a DIFFERENT amount of pressure depending on the fight: the Ten Shin
  // simul spec is stack_delta 1, but its solo_attack_spec loads 2 ("Loads +2 solo so
  // the pressure pill visibly climbs as the tell" — ten_shin_roster.py). The bare chip
  // reads as 1 either way, so solo Natrix was telling the player half the truth. Take
  // the magnitude from the serialized spec so the tooltip can never disagree with the
  // engine (Edward 2026-07-25).
  //
  // The magnitude renders as a `2 ×` PREFIX — `Inflicts 2 × [Pressure]` — reusing the
  // existing `.gt-formula` grammar (Edward 2026-07-26). It first shipped as a number
  // INSIDE the pill, which was a new surface invented for this one enemy when the
  // codebase already had a way to say "N of this tag": the Ten Shin aura row
  // (`num('2') op('×') dispense()`) and the warden Deals rows. That invention is
  // deleted; the input (the serialized delta) is unchanged.
  volume_warden: { entity: 'Volume Overload', attack: 'Volume Load', announce: [
    [t('Inflicts'), tagStack('pressure')],
  ] },
  // Escara is a CONVERTER, not an unconditional inflictor. Engine (batch_combat_
  // engine.py:1793) gates the permanent hit on consuming pressure: with pressure it
  // spends ONE stack to lock ONE end-organ (1:1 since 2026-07-14); with no pressure it
  // does nothing permanent, only its flat 2. So the row reads as the real cascade —
  // `Converts [pressure] into [end-organ]` — a two-chip row joined by the PLAIN word
  // "into" (no causal-arrow rail — Edward 2026-07-14). Kills the stale unconditional
  // "Inflicts +5" (magnitude AND framing were both wrong) and surfaces the pressure→
  // fibrosis conversion. Solo warden is unconditional by design, but shares this class
  // tooltip; the converter framing tracks the canonical Ten Shin simul.
  aldosterone_lock: { entity: 'Mineralocorticoid Excess', attack: 'Mineralocorticoid Remodeling', announce: [
    [j('Converts'), tag('pressure'), j('into'), tag('end-organ')],
  ], deals: [t('Deals'), num('2'), clin('fibrotic remodeling')] },
  // Sympra amplifies what the others build in the simul, but its self-contained solo
  // spec is a flat 4. Keep the product as the canonical/default row and override it
  // only when the serialized attack spec explicitly says fixed_four.
  adrenergic_sentry: { entity: 'Sympathetic Overdrive', attack: 'Sympathetic Surge', announce: [],
    deals: [t('Deals'), tag('pressure'), op('×'), tag('end-organ')],
    dealsByFormula: {
      fixed_four: [t('Deals'), num('4'), clin('steady sympathetic overdrive')],
    } },
  phantom: { entity: 'Comprehensive Board Exam', attack: 'Escalating Bite', announce: [
    [t('longer it drags'), edge('Bites'), t('harder')],
  ], deals: [t('Deals'), num('1.3'), op('×'), t('('), num('8'), op('+'), num('2'), op('×'), t('round'), t('), rounded down')] },

  // ── Simultaneous-pool entities (close-the-gap authoring 2026-06-11) ───────
  // These ship NO description from the backend (EntityState carries mechanics
  // as code, not copy) — the decode entry IS their tooltip content. Numbers
  // sourced from services/simultaneous_pool/*.py round/strike hooks.

  // Glyca — glucose gauge: the glucose-raising vs glucose-lowering hormone pair
  glycor: { entity: 'Glucagon (Hyperglycemia)', attack: 'Glucagon Surge', announce: [
    [t('Raises'), tag('glucose')],
  ] },
  insulax: { entity: 'Insulin', attack: 'Insulin Overshoot', announce: [
    [t('Lowers'), tag('glucose')],
  ] },
  resista: { entity: 'Insulin Resistance', attack: 'Insulin Resistance', announce: [
    [t('Builds'), tag('resistance')],
  ] },
  tollix: { entity: 'Microvascular Complications', attack: 'Microvascular Injury', announce: [
    [t('Stacks'), tag('microvascular')],
  ], deals: [t('Deals'), num('2'), op('×'), tag('microvascular')] },

  // Synap — neurotransmitter onset/tolerance
  // ⚠️ `Builds`, not `Escalates`. These were the file's only two verb/label collisions:
  // both `anhedrix-escalation` and `anxia-escalation` carry the LABEL "Escalation"
  // (enemyStatusMeta.js), so the row painted the stutter "Escalates Escalation".
  // `Builds` is this file's dominant enemy-charge verb (14 uses against 2).
  anhedrix: { entity: 'Major Depression', attack: 'Anhedonic Spiral', announce: [
    [t('Builds'), tag('anhedrix-escalation')],
    // `op('^')`, not `t('^')`: an exponent is a math operator and belongs in the muted
    // math tone with op('×') and uremos' op('÷'), not in the connective-text tone.
  ], deals: [t('Deals'), num('2'), op('^'), tag('anhedrix-escalation'), t('capped at'), num('32')] },
  lithia: { entity: 'Lithium Therapy', attack: 'Lithium Toxicity', announce: [
    [t('Runs'), tag('lithium')],
  ], deals: [t('Deals'), num('6'), clin('lithium toxicity'), t('per tick for 4 rounds')] },
  doppa: { entity: 'Antipsychotic EPS', attack: 'Dopamine Blockade', announce: [
    [t('Builds'), tag('eps')],
  ], deals: [t('Deals'), num('4'), clin('extrapyramidal rigidity')] },
  anxia: { entity: 'Anxiety Disorder', attack: 'Anxiety Escalation', announce: [
    [t('Builds'), tag('anxia-escalation')],
    [t('Builds'), tag('anxia-tolerance')],
  ], deals: [t('Deals'), num('1'), op('×'), tag('anxia-escalation'), op('+'), num('3'), clin('autonomic arousal')] },

  // Hemora — INR gauge + reversal token economy
  clottra: { entity: 'Thrombosis (Subtherapeutic INR)', attack: 'Thrombotic Drift', announce: [
    [t('Drags down'), tag('inr')],
    [t('Ramps'), tag('clot-ramp')],
  ] },
  hemorra: { entity: 'Hemorrhage (Supratherapeutic INR)', attack: 'Hemorrhagic Drift', announce: [
    [t('Drives up'), tag('inr')],
    [t('Ramps'), tag('bleed-ramp')],
  ] },
  cytox: { entity: 'Warfarin Drug Interactions', attack: 'Interaction Amplifier', announce: [
    [t('Swings'), tag('inr')],
    [t('Carries'), tag('ddi-amplifier')],
  ] },
  reverso: { entity: 'Anticoagulant Reversal', attack: 'Reversal Drain', announce: [
    [t('Steals'), tag('reversal-tokens')],
  ] },

  // Cephalgia — migraine phase cycle
  ictalus: { entity: 'Migraine Attack', attack: 'Migraine Attack', announce: [
    [t('Cycles'), tag('migraine-phase')],
  ], deals: [t('Deals'), num('10'), op('−'), num('2'), op('×'), tag('preventive'), t('min'), num('2'), op('·'), op('+'), num('4'), clin('rebound'), t('active')] },
  aura: { entity: 'Migraine Aura', attack: 'Cortical Aura', announce: [
    [t('your'), g('damage', '−10%'), j('while alive')],
  ] },
  chronify: { entity: 'Migraine Chronification', attack: 'Migraine Chronification', announce: [
    [t('Builds'), tag('preventive')],
  ] },
  rebound_cephalgia: { entity: 'Medication-Overuse Headache', attack: 'Rebound Headache', announce: [
    [t('spawned by 3 abortive overuses')],
    [t('2 clean rounds clears it')],
  ], deals: [t('Deals'), num('3'), clin('withdrawal headache'), t('per round')] },

  // Coelo — GI stewardship counters + spawned complications
  ulcra: { entity: 'H. pylori Peptic Ulcer', attack: 'Incomplete Eradication', announce: [
    [t('Demands'), tag('triple-course')],
    [t('Breeds'), tag('resistra-spawn')],
  ] },
  inflama: { entity: 'Inflammatory Bowel Disease', attack: 'Inflammatory Escalation', announce: [
    [t('Climbs'), tag('ibd-ladder')],
    [t('Spawns'), tag('opportuna-spawn')],
  ] },
  refluxa: { entity: 'GERD (Long-Term PPI Risk)', attack: 'Acid Suppression', announce: [
    [t('Spawns'), tag('cdif-spawn')],
    [t('Spawns'), tag('b12def-spawn')],
  ] },
  hepatix: { entity: 'Drug-Induced Liver Injury', attack: 'Hepatic Injury', announce: [
    [t('Accepts'), tag('hepatotoxic-match')],
  ], deals: [t('Deals'), num('6'), clin('hepatotoxic injury'), t('on forbidden classes')] },
  cdif: { entity: 'C. difficile Colitis', attack: 'Toxin Colitis', announce: [
    [t('spawned by PPI overuse')],
  ], deals: [t('Deals'), num('4'), clin('toxin-mediated colitis'), t('per round')] },
  resistra: { entity: 'Resistant H. pylori', attack: 'Resistant Regrowth', announce: [
    [t('resistant strain'), j('·'), t('spawned by partial courses')],
  ] },
  opportuna_tb: { entity: 'Latent TB Reactivation', attack: 'Latent Reactivation', announce: [
    [t('spawned by unscreened immunosuppression')],
  ], deals: [t('Deals'), num('5'), clin('reactivated tuberculosis'), t('per round')] },
  // Row 1 gains a lead verb, mirroring lyse's sanctioned bespoke case-2 shape: a raw
  // max-HP loss has no reusable chip, so the row stays flat, but a row with no verb at
  // all reads as a stray fragment. Both facts are engine-exact (coelo.py:206, :233-236).
  b12def: { entity: 'Vitamin B12 Deficiency', attack: 'Cobalamin Depletion', announce: [
    [t('spawned by long PPI use')],
    [t('Inflicts'), hp('−3 max'), j('on spawn')],
  ] },

  // Gravida — gestational clock windows
  cypher: { entity: 'Pregnancy CYP450 Changes', attack: 'Enzyme Induction', announce: [
    [t('Builds'), tag('inducer-load')],
  ] },
  // 🔴 NEITHER OF THESE ADVANCES THE CLOCK. The POOL's own round hook does
  // (gravida.py:384-386, registered :443); embra and latera only read the week to decide
  // whether they are dangerous, so `Advances` credited both with a mechanic they do not
  // own AND said the same thing on two cards that behave oppositely. Each now states its
  // OWN window, and the trailing token becomes the right column on the enemy-status
  // branch (which, unlike the player-status branch, keeps what follows the chip).
  // `Runs` is the registered enemy-gauge verb; `Shielded` is the house verb for
  // `damage_in_modifier -> 0.0` and is the engine's own zone string (gravida.py:156).
  // Warrants: EMBRA_VULNERABLE_THROUGH_WEEK = 13, LATERA_VULNERABLE_FROM_WEEK = 20
  // (gravida.py:23, :24, gating at :153-154 and :331-337).
  embra: { entity: 'First-Trimester Teratogenesis', attack: 'Teratogenic Exposure', announce: [
    [t('Runs'), tag('gestational-week'), t('vulnerable through week 13')],
  ], deals: [t('Deals'), num('6'), clin('teratogenic exposure'), t('on forbidden classes')] },
  latera: { entity: 'Third-Trimester Drug Risk', attack: 'Fetal Toxicity', announce: [
    [t('Shielded'), tag('gestational-week'), t('before week 20')],
  ], deals: [t('Deals'), num('6'), clin('late-gestation toxicity'), t('on forbidden classes')] },
  // lacta carries `gestational-week` in her OWN payload (gravida.py:230-231 -> :172-183),
  // so the chip is hers rather than borrowed, and the digit comes out of the voice.
  // LACTA_SPAWN_WEEK = 38 (gravida.py:22, spawned :396-397).
  lacta: { entity: 'Lactation Drug Safety', attack: 'Lactation Exposure', announce: [
    [t('Spawns at'), tag('gestational-week'), t('week 38')],
  ], deals: [t('Deals'), num('6'), clin('lactation exposure'), t('on forbidden classes')] },

  // Mitos — chemo drug classes (division minis) + their signature toxicities
  anthrox: { entity: 'Anthracyclines', attack: 'Anthracycline Division', announce: [
    [tag('division')],
    [t('Builds'), tag('sublethal-division')],
  ] },
  vincra: { entity: 'Vinca Alkaloids', attack: 'Mitotic Escape', announce: [
    [tag('division')],
    [t('Builds'), tag('sublethal-division')],
  ] },
  antimet: { entity: 'Antimetabolites', attack: 'Antimetabolite Division', announce: [
    [tag('division')],
    [t('Builds'), tag('sublethal-division')],
  ] },
  anthrox_daughter: { entity: 'Anthracycline Cardiotoxicity', attack: 'Cardiotoxic Clone', announce: [
    [t('clonal daughter'), j('·'), t('spawned by division')],
  ] },
  vincra_daughter: { entity: 'Vincristine Neuropathy', attack: 'Neurotoxic Clone', announce: [
    [t('clonal daughter'), j('·'), t('spawned by division')],
  ] },
  antimet_daughter: { entity: 'Methotrexate Toxicity', attack: 'Antimetabolite Clone', announce: [
    [t('clonal daughter'), j('·'), t('spawned by division')],
  ] },
  resistra_clone: { entity: 'Chemoresistant Clone', attack: 'Resistant Expansion', announce: [
    [t('resistant clone'), j('·'), t('spawned at 2 divisions')],
  ] },

  // Nephra — renal gauges
  filtra: { entity: 'Declining GFR', attack: 'Filtration Decline', announce: [
    [t('Declines'), tag('gfr')],
  ] },
  electra: { entity: 'Potassium Dysregulation', attack: 'Potassium Arrhythmia', announce: [
    [t('Drifts'), tag('enemy-potassium')],
  ], deals: [t('Deals'), num('4'), clin('arrhythmia'), t('outside'), tag('enemy-potassium'), t('safe range')] },
  uremos: { entity: 'Uremia', attack: 'Uremic Accumulation', announce: [
    [t('Builds'), tag('uremia')],
  ], deals: [t('Deals'), tag('uremia'), op('÷'), num('2'), t('rounded down')] },
  pharmagic: { entity: 'Renal Drug Accumulation', attack: 'Drug Accumulation', announce: [
    [t('Builds'), tag('accumulation')],
  ] },

  // Chroni — COPD obstruction, air trapping, gas exchange
  // `obstruction` never clears to zero: it floors at OBSTRUCTION_IRREVERSIBLE_FLOOR
  // (chroni.py), which is the asthma/COPD distinction made mechanical. The line says
  // "relieved, never cleared" for exactly that reason — do not soften it to "cleared".
  obstructa: { entity: 'Airflow Obstruction', attack: 'Airway Narrowing', announce: [
    [t('Builds'), tag('obstruction')],
  ], deals: [t('Deals'), tag('obstruction')] },
  // No `deals` row: trapping deals no direct damage, it chokes the player's damage OUT.
  // Same shape as Pulm's retired `sputa` and Visia's `sicca`.
  trappa: { entity: 'Air Trapping', attack: 'Hyperinflation', announce: [
    [t('Builds'), tag('air-trapping')],
  ] },
  flara: { entity: 'Acute Exacerbation', attack: 'Exacerbation Burst', announce: [
    [t('Charges'), tag('exacerbation')],
  ], deals: [t('Deals'), num('9'), clin('acute decompensation'), t('at'), tag('exacerbation'), t('cap')] },
  // Two-sided gauge: BOTH ends damage, so the deals row names respiratory failure rather
  // than desaturation, which would describe only the hypoxemic half.
  capnia: { entity: 'Impaired Gas Exchange', attack: 'Gas Exchange Failure', announce: [
    [t('Drifts'), tag('gas-exchange')],
  ], deals: [t('Deals'), num('4'), clin('respiratory failure'), t('outside'), tag('gas-exchange'), t('target')] },
  corpulmo: { entity: 'Cor Pulmonale', attack: 'Right Heart Strain', announce: [
    [t('spawned by 3 hypoxemic rounds')],
    [t('Inflicts'), hp('−3 max'), j('on spawn')],
  ], deals: [t('Deals'), num('3'), clin('right heart strain'), t('per round')] },

  // Visia — ocular gauges
  glauca: { entity: 'Glaucoma', attack: 'Optic Pressure', announce: [
    [t('Climbs'), tag('iop')],
    // The parens and `ceil((` are OPERATORS, not connective text. As `t()` they broke
    // the formula run into fragments, so the expression rendered in three different
    // tones with uneven gaps; as `op()` the whole thing sits in one `.gt-formula` with
    // tabular numerals, the way phantom's already does. The strings are byte-identical.
    // ⚠️ 58 of the 59-glyph authored-line budget. Do not lengthen this row.
  ], deals: [t('Deals'), op('ceil(('), tag('iop'), op('−'), num('24'), op(')'), op('÷'), num('3'), op(')'), op('·'), num('4'), clin('uncounseled beta-blocker')] },
  // `Accepts`, not `Match`. Every other enemy row in this file describes what the ENEMY
  // does, in the third person; `Match` was the one imperative, addressing the reader
  // from a card whose siblings do not. `Accepts` is the registered enemy-match verb
  // (enemyStatusMeta.js) and already ships on the only other match row, hepatix. It also
  // kills the "Match … Etiology" word repetition.
  conjuncta: { entity: 'Conjunctivitis', attack: 'Etiology Mismatch', announce: [
    [t('Accepts'), tag('etiology')],
  ], deals: [t('Deals'), num('5'), clin('mistreatment'), t('when'), tag('etiology'), t('is mismatched')] },
  sicca: { entity: 'Dry Eye Disease', attack: 'Tear-Film Veil', announce: [
    [t('Builds'), tag('veil')],
  ] },
};

// Legacy monoclonal alias slugs still shipped by some anaphyl encounters
// (backend MINI_BOSS_ALIASES) — resolve to the canonical mini.
const SLUG_ALIASES = {
  'murin-omab': 'hyst',
  'chimer-ximab': 'lyse',
  'huma-zumab': 'plex',
  'pur-umab': 'latanz',
  // Ten Shin axis wardens: the HTN class-mini path ships boss_class (resolves via
  // cls), but map the display names too so nameSlug resolution also lands the
  // warden's decode regardless of which identity field the encounter carries.
  // Prefer canonical Angio while retaining Angira for legacy persisted/anon sessions.
  angio: 'raas_warden',
  angira: 'raas_warden',
  calix: 'calcium_gate',
  natrix: 'volume_warden',
  escara: 'aldosterone_lock',
  sympra: 'adrenergic_sentry',
};

/* Slugify a display name to a decode key — minis are named exactly as their
   slug ("Eldra" → eldra, "Lipo-Alpha" → lipo-alpha), so this recovers identity
   on the single-enemy path where the backend ships only boss_name (no slug):
   boss_name="Eldra", boss_class="athero" → must resolve to LDL, not the pool. */
function slugifyName(name) {
  return String(name || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/* Resolve the decode entry for an enemy by specificity:
   slug (mini identity) → slugified name (single-enemy fights ship only a name)
   → boss_class (pool/main identity). A boss's elaborate name ("Athero, the
   Reaper") won't match a key, so it correctly falls through to its pool class. */
export function decodeForEnemy(enemy = {}) {
  const rawSlug = String(enemy.slug || '').toLowerCase();
  const slug = SLUG_ALIASES[rawSlug] || rawSlug;
  const rawNameSlug = slugifyName(enemy.name);
  const nameSlug = SLUG_ALIASES[rawNameSlug] || rawNameSlug;
  const cls = String(enemy.boss_class || '').toLowerCase();
  return ENEMY_DECODE[slug] || ENEMY_DECODE[nameSlug] || ENEMY_DECODE[cls] || null;
}
